import React from 'react';
import { Handle, Position, useReactFlow, type NodeProps } from '@xyflow/react';

export interface NoteNodeData {
    label: string;
    [key: string]: unknown;
}

/**
 * Note Node
 *
 * A free-text annotation node. Unlike Fragment/Comment/Hub/Group, it had
 * *no* editing path at all before this fix — no on-canvas double-click
 * handler, and no matching case in Inspector.tsx's type-specific sections
 * (which cover 'fragment', 'comment', 'hub'/'group', but not 'note'). Every
 * Note was permanently stuck at its creation-time default label with zero
 * way to change it. Fixed the same way fragment.tsx was: double-click opens
 * an expanded editor, committed via `setNodes`.
 */
export function NoteNode({ data, selected, id }: NodeProps) {
    const nodeData = data as NoteNodeData;
    const { setNodes } = useReactFlow();
    const [isEditing, setIsEditing] = React.useState(false);
    const [tempLabel, setTempLabel] = React.useState(nodeData.label);

    const handleSaveEdit = () => {
        setNodes((nodes) =>
            nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, label: tempLabel } } : n))
        );
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div
                className="nopan nodrag card card-sm shadow-lg"
                style={{ minWidth: 220 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="card-body py-2 px-3">
                    <label className="form-label small mb-1">Note</label>
                    <textarea
                        value={tempLabel}
                        onChange={(e) => setTempLabel(e.target.value)}
                        className="form-control form-control-sm"
                        rows={3}
                        autoFocus
                    />
                    <div className="d-flex gap-2 mt-2">
                        <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="btn btn-sm btn-success flex-fill"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setTempLabel(nodeData.label);
                                setIsEditing(false);
                            }}
                            className="btn btn-sm btn-outline-secondary flex-fill"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
            </div>
        );
    }

    return (
        <div
            className={`card card-sm ${selected ? 'border-primary shadow-sm' : ''}`}
            style={{ minWidth: 180 }}
            onDoubleClick={() => setIsEditing(true)}
        >
            <div className="card-body py-2 px-3">
                <div className="d-flex align-items-center gap-2 text-muted mb-1">
                    <i className="ti ti-note" />
                    <span className="text-uppercase small">Note</span>
                </div>
                <div className="small nodrag">{nodeData.label ?? 'Untitled note'}</div>
            </div>
            <Handle type="target" position={Position.Left} />
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

export const noteNodeMeta = {
    type: 'note' as const,
    label: 'Note',
    icon: 'ti-note',
    defaultData: { label: 'New note' } satisfies NoteNodeData,
};
